import { guardNumber } from "../numbers";

interface Coordinate {
	latitude: number | null;
	longitude: number | null;
}

interface GeometriesCenter extends Coordinate {
	latitude: number;
	longitude: number;
	delta: number;
}

const DEFAULT_DELTA = 0.3;
const MIN_DELTA = 5;

export function getGeometriesCenter(_geometries: Array<Coordinate>): GeometriesCenter {
	const geometries = _geometries.filter((geo) => !!geo.latitude && !!geo.longitude) as Array<
		Required<Coordinate>
	>;
	const count = geometries.length;
	if (count === 0) return { latitude: 52.5028, longitude: 13.4042, delta: DEFAULT_DELTA };
	if (count === 1) {
		return {
			latitude: guardNumber(geometries[0]?.latitude),
			longitude: guardNumber(geometries[0]?.longitude),
			delta: DEFAULT_DELTA,
		};
	}

	let x = 0;
	let y = 0;
	let z = 0;

	let minLat = Number.POSITIVE_INFINITY;
	let maxLat = Number.NEGATIVE_INFINITY;
	let minLon = Number.POSITIVE_INFINITY;
	let maxLon = Number.NEGATIVE_INFINITY;

	for (const geometry of geometries) {
		const latitude = guardNumber(geometry.latitude);
		const longitude = guardNumber(geometry.longitude);
		const lat = (guardNumber(latitude) * Math.PI) / 180;
		const lon = (guardNumber(longitude) * Math.PI) / 180;
		x += Math.cos(lat) * Math.cos(lon);
		y += Math.cos(lat) * Math.sin(lon);
		z += Math.sin(lat);

		if (latitude < minLat) minLat = latitude;
		if (latitude > maxLat) maxLat = latitude;
		if (longitude < minLon) minLon = longitude;
		if (longitude > maxLon) maxLon = longitude;
	}

	x /= count;
	y /= count;
	z /= count;

	const longitude = (Math.atan2(y, x) * 180) / Math.PI;
	const hyp = Math.sqrt(x * x + y * y);
	const latitude = (Math.atan2(z, hyp) * 180) / Math.PI;

	const delta = Math.min(Math.max(maxLat - minLat, maxLon - minLon) * 1.2, MIN_DELTA);

	return { latitude, longitude, delta };
}
